<template>
  <div class="rm rm-theme">
    <!-- Početna: prikaz i izbor grafikona -->
    <template v-if="!section">
      <div class="rm-home-header">
        <span v-if="accountName" class="rm-muted">{{ accountName }}</span>
        <h1 class="rm-home-header__title">{{ $t("routes.statistics") }}</h1>
      </div>

      <div class="rm-chip-row rm-home-periods">
        <button
          v-for="preset in quickPresets"
          :key="preset.key"
          type="button"
          class="rm-chip rm-chip--lg"
          :class="{ 'rm-chip--active': activePresetKey === preset.key }"
          :aria-pressed="String(activePresetKey === preset.key)"
          @click="setPeriod(preset)"
        >
          {{ presetChipLabel(preset) }}
        </button>
        <button
          v-if="!activeQuickPreset"
          type="button"
          class="rm-chip rm-chip--lg rm-chip--active rm-num"
          aria-pressed="true"
          @click="periodSheet = true"
        >
          {{ periodShort }}
        </button>
        <button
          type="button"
          class="rm-chip rm-chip--lg rm-chip--icon"
          :aria-label="$t('report_mobile.choose_period')"
          @click="periodSheet = true"
        >
          <v-icon size="20" color="#1c1917">mdi-calendar-blank-outline</v-icon>
        </button>
      </div>

      <div v-if="views.length > 1" class="rm-chip-row st-views" role="group" :aria-label="$t('view')">
        <button
          v-for="item in views"
          :key="item.id"
          type="button"
          class="rm-chip"
          :class="{ 'rm-chip--active': selectedView === item.id }"
          :aria-pressed="String(selectedView === item.id)"
          @click="setView(item.id)"
        >
          {{ item.name }}
        </button>
      </div>

      <div v-if="!view" class="rm-empty">
        <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
      </div>
      <div v-else class="rm-home-groups">
        <section class="rm-home-group">
          <div class="rm-card">
            <button v-for="item in sections" :key="item.key" type="button" class="rm-menu-row" @click="openScreen(item.key)">
              <span class="rm-menu-row__icon">
                <v-icon size="20" color="#c2410c">{{ item.icon }}</v-icon>
              </span>
              <span class="rm-menu-row__text">
                <span class="rm-menu-row__title">{{ item.title }}</span>
              </span>
              <v-icon size="18" color="#8a817a">mdi-chevron-right</v-icon>
            </button>
          </div>
        </section>
      </div>
    </template>

    <!-- Pojedinačni grafikon -->
    <template v-else>
      <div class="rm-detail-top">
        <div class="rm-detail-header">
          <button type="button" class="rm-icon-btn" :aria-label="$t('report_mobile.back')" @click="closeScreen">
            <v-icon size="24" color="#1c1917">mdi-chevron-left</v-icon>
          </button>
          <div class="rm-detail-header__text">
            <h1 class="rm-detail-header__title">{{ section.title }}</h1>
            <span class="rm-muted rm-detail-header__sub">{{ headerSubtitle }}</span>
          </div>
          <button type="button" class="rm-icon-btn" :aria-label="$t('report_mobile.refresh')" @click="reload">
            <v-icon size="22" color="#1c1917">mdi-refresh</v-icon>
          </button>
        </div>

        <div class="rm-chip-row">
          <button type="button" class="rm-chip" @click="periodSheet = true">
            <span class="rm-num">{{ periodShort }}</span>
            <v-icon size="16" color="#5f5852">mdi-chevron-down</v-icon>
          </button>
        </div>
      </div>

      <div v-if="!view" class="rm-empty">
        <v-progress-circular indeterminate color="#c2410c"></v-progress-circular>
      </div>
      <div v-else-if="!view.feed" class="rm-empty">
        <v-icon size="40" color="#8a817a">mdi-sitemap-outline</v-icon>
        {{ $t("statistics_company_multi_restaurant_placeholder") }}
        <span class="rm-muted">{{ $t("statistics_coming_soon") }}</span>
      </div>
      <div v-else class="rm-screen st-screen">
        <div v-if="kpis.length" class="rm-stats">
          <div v-for="(stat, index) in kpis" :key="stat.key" class="rm-stat" :class="{ 'rm-stat--inverse': index === 0 }">
            <span class="rm-stat__label">{{ stat.label }}</span>
            <span class="rm-stat__value rm-num">{{ stat.value }}</span>
          </div>
        </div>
        <div class="rm-card st-mcard" :aria-busy="String(loading)">
          <div v-if="loading" class="st-loadbar" role="progressbar" :aria-label="$t('loading')"></div>
          <component
            :is="view.feed"
            :key="`${view.id}-${reloadKey}`"
            :date="dateRange"
            :section="currentKey"
            :charts="view.charts"
            @kpis="kpis = $event"
            @loading="loading = $event"
          ></component>
        </div>
      </div>
    </template>

    <report-mobile-period-sheet
      v-model="periodSheet"
      :from="from"
      :to="to"
      :presets="presets"
      @apply="setPeriod"
    ></report-mobile-period-sheet>
  </div>
</template>

<script>
import ReportMobilePeriodSheet from "@/components/report/mobile/ReportMobilePeriodSheet.vue";
import "@/components/report/mobile/reportMobileShell.scss";
import "../statisticsReport.scss";
import { findPresetKey, formatRange, formatShortDate } from "@/components/report/shared/periods";
import { QUERY_KEYS } from "@/components/report/desktop/reportQuery";
import hasStatisticsState from "../hasStatisticsState";

/**
 * Statistike na telefonu: početna sa prikazom i izborom grafikona, pa grafikon u kartici sa brojkama, periodom i
 * osvežavanjem. Isti stil i ponašanje kao mobilni izveštaji. Podatke učitava stari tab (nasleđen u statsFeeds),
 * grafikoni su postojeći (prilagođavaju se širini ekrana).
 */
export default {
  name: "StatisticsMobile",
  mixins: [hasStatisticsState],
  components: { ReportMobilePeriodSheet },
  data: () => ({
    periodSheet: false,
    reloadKey: 0,
    pushedScreens: 0,
    kpis: [],
    loading: false,
  }),
  computed: {
    accountName() {
      const { company, restaurant } = this.$store.state;
      return this.toLatin((this.kind === "restaurant" ? restaurant?.name : company?.name) || "");
    },
    /** Na telefonu je početna bez ključa u adresi: grafikon je otvoren samo kad adresa ima ispravan ?r=. */
    currentKey() {
      const wanted = this.$route.query[QUERY_KEYS.report];
      return this.menuKeys.includes(wanted) ? wanted : "";
    },
    quickPresets() {
      return this.presets.filter((preset) => preset.key !== "last_month");
    },
    activePresetKey() {
      return findPresetKey(this.presets, this.from, this.to);
    },
    activeQuickPreset() {
      return this.quickPresets.some((preset) => preset.key === this.activePresetKey);
    },
    /** Datum, a kod restorana i prikaz (isti grafikon postoji u više prikaza). */
    headerSubtitle() {
      return this.views.length > 1 && this.view ? `${this.periodLong} · ${this.view.name}` : this.periodLong;
    },
    periodShort() {
      return this.activePresetKey ? this.$t(`report_mobile.presets.${this.activePresetKey}`) : formatRange(this.from, this.to);
    },
  },
  watch: {
    urlQuery: {
      handler(query) {
        this.writeUrl(query);
      },
      deep: true,
    },
    selectedView() {
      this.kpis = [];
    },
    currentKey(key, previous) {
      if (key !== previous) {
        this.kpis = [];
        window.scrollTo(0, 0);
      }
    },
  },
  mounted() {
    this.writeUrl(this.urlQuery);
  },
  methods: {
    presetChipLabel(preset) {
      const label = this.$t(`report_mobile.presets.${preset.key}`);
      if (this.activePresetKey === preset.key && preset.from === preset.to) {
        return `${label} · ${formatShortDate(preset.from)}`;
      }
      return label;
    },
    openScreen(key) {
      if (!this.menuKeys.includes(key) || key === this.currentKey) {
        return;
      }
      this.pushedScreens += 1;
      this.$router.push({ query: { ...this.$route.query, [QUERY_KEYS.report]: key } }).catch(() => {});
    },
    closeScreen() {
      // Grafikon otvoren sa ove stranice → nazad kroz istoriju (radi i hardverski "nazad");
      // direktan link (?r=...) → zameni adresu, da ne izađemo sa /statistics.
      if (this.pushedScreens > 0) {
        this.pushedScreens -= 1;
        this.$router.back();
        return;
      }
      const query = { ...this.$route.query };
      delete query[QUERY_KEYS.report];
      this.$router.replace({ query }).catch(() => {});
    },
    reload() {
      this.reloadKey += 1;
    },
  },
};
</script>
